import React, { useEffect, useMemo, useState } from 'react'
import '../stylesheets/ActivitiesPage.scss'
import { OnboardingPrompt } from 'components/UIElements'
import PendingInstrumentsList from './PendingInstrumentsList'
import { INSTRUMENT_DISPLAY_LOCATION_OPTIONS } from 'utils/constants'
import { getCaregiverSelectionFlags } from 'utils/caregiverUtils'
import ParticipantSelectionOverlay from 'components/ParticipantSelectionOverlay'
import { calendarIcon, carouselIcon } from '../../../assets/assets'
import CalendarInstrumentsList from './CalendarInstrumentsList'
import moment from 'moment'
import { getLine } from '../utils/calendarUtils'
import BlockingConsentModal from '../../Consent/components/BlockingConsentModal'
import { isNonMobileSchedulePlatform, getSchedulesList } from '../utils/instrumentUtils'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'

const DISPLAYING_MODE = {
  carousel: 'carousel',
  calendar: 'calendar',
}

const ActivitiesPage = (props) => {
  const {
    isInstrumentArch1,
    resetWebSurvey,
    resetCurrentSurvey,
    resetSubmissionSurvey,
    onboarding,
    strings,
    pendingInstruments,
    instruments = [],
    allInstruments,
    allInstances = [],
    futureInstances = [],
    availableInstances = [],
    formatSubmissionSurvey,
    sendEvent,
    selectedLocale,
    showConsentModal,
    fetchInstrument,
    setSubmissionSurvey,
    humiIsabellaDisabled,
    studyId,
    firstLogin,
  } = props
  const [onboarded, setOnboarded] = useState(false)
  const [selectedMode, setSelectedMode] = useState(DISPLAYING_MODE.carousel)
  const [monthList, setMonthList] = useState([])
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedDay, setSelectedDay] = useState()
  const [page, setPage] = useState(0)
  const [selectedLine, setSelectedLine] = useState(0)

  const webScheduleInstances = useMemo(
    () => allInstances.filter(isNonMobileSchedulePlatform),
    [allInstances],
  )
  const webFutureInstances = useMemo(
    () => futureInstances.filter(isNonMobileSchedulePlatform),
    [futureInstances],
  )
  const webAvailableInstances = useMemo(
    () => availableInstances.filter(isNonMobileSchedulePlatform),
    [availableInstances],
  )

  const findMonthIndex = (list, day = moment()) => {
    const targetMonth = day.month() + 1
    const targetYear = day.year()
    return list.findIndex(
      (item) => item?.value === targetMonth && item?.year === targetYear,
    )
  }

  const generateMonthList = (locale = 'en') => {
    const previousLocale = moment.locale()
    moment.locale(locale)

    const months = []
    const instancesDates = webScheduleInstances
      .flatMap(({ instances }) => instances)
      .map(({ start_date }) => start_date)
      .sort((a, b) => new Date(a) - new Date(b))

    const dateStart = moment(instancesDates[0] || firstLogin)
    const futureDate = instancesDates.length
      ? new Date(instancesDates[instancesDates.length - 1])
      : new Date()
    const currentDate = new Date()
    const yearDifference = futureDate.getFullYear() - currentDate.getFullYear()
    const monthDifference = futureDate.getMonth() - currentDate.getMonth()
    const totalMonthsDifference = yearDifference * 12 + monthDifference + 1

    const dateEnd = moment().add(
      totalMonthsDifference > 2 ? totalMonthsDifference : 2,
      'month',
    )

    while (dateStart.isBefore(dateEnd, 'month')) {
      const monthDays = new Array(dateStart.daysInMonth())
        .fill(null)
        .map((_, i) => moment(dateStart).startOf('month').add(i, 'days'))

      months.push({
        // Use numeric getters — Arabic locale formats MM/YYYY as Eastern digits (NaN).
        value: dateStart.month() + 1,
        shortName: dateStart.format('MMM'),
        longName: dateStart.format('MMMM'),
        days: monthDays,
        year: dateStart.year(),
      })

      dateStart.add(1, 'month')
    }

    moment.locale(previousLocale)
    return months
  }

  const selectMonth = (month) => {
    setSelectedMonth(month)
  }

  const selectDay = (day) => {
    setSelectedDay(day)
  }

  const selectLine = (line) => {
    setSelectedLine(line)
  }

  const filterMonthListByInstruments = (list) => {
    return list
  }

  useEffect(() => {
    const generatedMonthList = generateMonthList(selectedLocale)

    if (!generatedMonthList?.length) return

    const filteredMonthList = filterMonthListByInstruments(generatedMonthList)
    const today = moment()
    const currentMonthIndex = findMonthIndex(filteredMonthList, today)
    const resolvedMonthIndex =
      currentMonthIndex >= 0 ? currentMonthIndex : 0

    // Set month list, selected month, page, day, and week line together so
    // week view never renders with selectedDay set but selectedMonth empty
    // (that combination hides every month via showOnlyCurrentMonth).
    setMonthList(filteredMonthList)
    selectMonth(filteredMonthList[resolvedMonthIndex])
    setPage(Math.floor(resolvedMonthIndex / 2))
    selectDay(today)
    selectLine(getLine(today.date() - 1))
  }, [selectedLocale, webScheduleInstances, firstLogin])

  const getDisplayingMonthList = () => {
    return monthList.slice(page * 2, page * 2 + 2)
  }

  const displayingMonthList = getDisplayingMonthList()

  const onLeftArrowClick = () => {
    selectDay('')
    setPage(page - 1)
  }

  const onRightArrowClick = () => {
    selectDay('')
    setPage(page + 1)
  }

  // useEffect(() => {
  //   if (displayingMonthList?.length) {
  //     selectMonth(displayingMonthList[0])
  //   }
  // }, [displayingMonthList])

  const onClose = () => {
    setOnboarded(true)
  }

  const getFormattedList = () => {
    if (isInstrumentArch1) {
      return [...pendingInstruments, ...allInstruments]
    }
    if (selectedMode === DISPLAYING_MODE.calendar) {
      return [...pendingInstruments, ...allInstruments]
    }
    const combinedList = [...pendingInstruments, ...instruments]
    return combinedList
  }

  const filterByPlatformType = (list = []) =>
    list.filter(isNonMobileSchedulePlatform)

  const getCarouselList = (list) => {
    const filteredByDisplayLocation = list?.filter((item) => {
      if (item?.displayLocation) {
        const location = String(item.displayLocation).toUpperCase()
        return location.includes(INSTRUMENT_DISPLAY_LOCATION_OPTIONS.carousel)
      }
      return true
    })

    // Note: Visibility time range checking for carousel is handled when instruments are fetched
    // via filterInstrumentsByVisibiity. Formatted instruments don't include visibility metadata,
    // so we rely on the initial filtering. The main fix is in CalendarMonthListItem for calendar view.
    return filteredByDisplayLocation
  }

  const getCalendarList = (list) => {
    const filteredByDisplayLocation = list?.filter((item) => {
      if (item?.displayLocation) {
        const location = String(item.displayLocation).toUpperCase()
        return location.includes(INSTRUMENT_DISPLAY_LOCATION_OPTIONS.calendar)
      }
      return true
    })

    return filteredByDisplayLocation
  }

  const formattedList = filterByPlatformType(getFormattedList())

  const getDisplayingList = () => {
    if (selectedMode === DISPLAYING_MODE.calendar) {
      return getCalendarList(formattedList)
    }
    return getCarouselList(formattedList)
  }

  const displayingList = getDisplayingList()

  useEffect(() => {
    resetWebSurvey()
    resetCurrentSurvey()
    resetSubmissionSurvey()
  }, [])

  // useEffect(() => {
  //   if (
  //     !getCarouselList(formattedList)?.length &&
  //     selectedMode === DISPLAYING_MODE.carousel
  //   ) {
  //     setSelectedMode(DISPLAYING_MODE.calendar)
  //   }
  // }, [])

  const getDatesArray = () => {
    const datesArray = []

    webScheduleInstances.forEach((instance) => {
      const schedules = getSchedulesList(instance?.metadata?.schedule)

      schedules.forEach((schedule) => {
        if (schedule?.deploy?.absolute) {
          const date = moment.unix(schedule.deploy.absolute)
          const deployMonth = moment.utc(date)

          datesArray.push({
            value: deployMonth.month() + 1,
            name: deployMonth.format('MMM'),
            year: deployMonth.year(),
            fullDate: deployMonth,
          })
        }

        if (schedule?.expire?.absolute) {
          const date = moment.unix(schedule.expire.absolute)
          const expireMonth = moment.utc(date)

          datesArray.push({
            value: expireMonth.month() + 1,
            name: expireMonth.format('MMM'),
            year: expireMonth.year(),
            fullDate: expireMonth,
          })
        }
      })

      const completedInstance = instance?.instances.find(
        (inst) => !!inst.completion,
      )
      if (completedInstance) {
        const completedMonth = moment.utc(completedInstance?.completion)

        datesArray.push({
          value: completedMonth.month() + 1,
          name: completedMonth.format('MMM'),
          year: completedMonth.year(),
          fullDate: completedMonth,
        })
      }
    })

    return datesArray
  }

  const getEarliestMonth = () => {
    const dates = getDatesArray()

    return dates.reduce(
      (acc, curr) => (curr.value < acc.value ? curr : acc),
      dates[0] || undefined,
    )
  }

  const getLatestMonth = () => {
    const dates = getDatesArray()

    const date = dates.reduce(
      (acc, curr) => (curr.value > acc.value ? curr : acc),
      dates[0] || undefined,
    )
    const momentDate = moment(date?.fullDate).add(2, 'month')

    return {
      value: momentDate.month() + 1,
      name: momentDate.format('MMM'),
      year: momentDate.year(),
      fullDate: momentDate,
    }
  }

  const checkIfLeftArrowDisabled = () => {
    const earliest = getEarliestMonth()

    const displayingMonth = displayingMonthList.find(
      (item) =>
        item?.value === earliest?.value && item?.year === earliest?.year,
    )

    if (displayingMonth) {
      return true
    }

    return page === 0
  }

  const checkIfRightArrowDisabled = () => {
    const latest = getLatestMonth()

    const displayingMonth = displayingMonthList.find(
      (item) => item?.value === latest?.value && item?.year === latest?.year,
    )

    if (displayingMonth) {
      return true
    }

    const lastDisplayingMonth =
      displayingMonthList[displayingMonthList?.length - 1]
    const lastMonth = monthList[monthList?.length - 1]

    return (
      lastDisplayingMonth?.value === lastMonth?.value &&
      lastDisplayingMonth?.year === lastMonth?.year
    )
  }

  const leftArrowDisabled = checkIfLeftArrowDisabled()

  const rightArrowDisabled = checkIfRightArrowDisabled()

  const { mustSelectParticipant } = getCaregiverSelectionFlags({
    user: props?.user,
    caregiver: props?.caregiver,
  })

  // overlay is shared via ParticipantSelectionOverlay component

  return (
    <div className='home page'>
      <BlockingConsentModal
        showConsentModal={showConsentModal}
        strings={strings}
      />
      {!showConsentModal && (
        <>
          <div className='activities-title-panel'>
            <p>{strings.activities}</p>
            <div className='activities-modes-section'>
              <div
                className='activities-mode-option'
                onClick={() => {
                  setSelectedMode(DISPLAYING_MODE.carousel)
                }}>
                {carouselIcon(selectedMode === DISPLAYING_MODE.carousel)}
              </div>
              <div
                className='activities-mode-option'
                onClick={() => {
                  setSelectedMode(DISPLAYING_MODE.calendar)
                  logSentryEvent({
                    message: SENTRY_MESSAGES.calendarOpened,
                    tags: { flow: 'visits', action: 'calendar_opened' },
                  })
                }}>
                {calendarIcon(selectedMode === DISPLAYING_MODE.calendar)}
              </div>
            </div>
          </div>

          <div className='linkt-container'>
            {mustSelectParticipant && <ParticipantSelectionOverlay />}
            {onboarding && !onboarded && (
              <OnboardingPrompt
                type='activities'
                closeFunc={onClose}
                strings={strings}
                humiIsabellaDisabled={humiIsabellaDisabled}
              />
            )}

            {selectedMode === DISPLAYING_MODE.carousel ? (
              <PendingInstrumentsList
                {...props}
                displayingList={displayingList}
              />
            ) : null}

            {selectedMode === DISPLAYING_MODE.calendar ? (
              <CalendarInstrumentsList
                {...props}
                displayingList={displayingList}
                allInstances={webScheduleInstances}
                futureInstances={webFutureInstances}
                availableInstances={webAvailableInstances}
                monthList={displayingMonthList}
                fullMonthList={monthList}
                selectMonth={selectMonth}
                selectedMonth={selectedMonth}
                selectDay={selectDay}
                selectedDay={selectedDay}
                formatSubmissionSurvey={formatSubmissionSurvey}
                strings={strings}
                sendEvent={sendEvent}
                selectedLocale={selectedLocale}
                fetchInstrument={fetchInstrument}
                setSubmissionSurvey={setSubmissionSurvey}
                humiIsabellaDisabled={humiIsabellaDisabled}
                onLeftArrowClick={onLeftArrowClick}
                onRightArrowClick={onRightArrowClick}
                leftArrowDisabled={leftArrowDisabled}
                rightArrowDisabled={rightArrowDisabled}
                studyId={studyId}
                selectedLine={selectedLine}
                selectLine={selectLine}
                firstLogin={firstLogin}
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

export default ActivitiesPage
